import { z } from 'zod';

// The theme settings that change what a shop does rather than how it is painted — Shopify's Product cards,
// Cart and Search groups. They ride beside the colours and fonts in a theme's styles, so a store overrides
// one the way it repaints a colour and a theme update reaches every option the store left alone.

/** The shapes a product card's picture can take. */
export const CARD_RATIOS = ['square', 'portrait', 'landscape'] as const;

/** How the cart's parts are parted: by a hairline, or by a double line as a printed page rules its columns. */
export const CART_RULES = ['hairline', 'double'] as const;

/** Each option is optional: one nobody set means what every shop did before options existed. */
export const ThemeOptionsSchema = z.object({
  /** How wide the header's logo draws, in pixels; 0 or none leaves the theme's own size. */
  logoWidth: z.number().int().min(0).max(400).optional(),
  cardRatio: z.enum(CARD_RATIOS).optional(),
  cardHoverImage: z.boolean().optional(),
  cardShowType: z.boolean().optional(),
  cardShowRating: z.boolean().optional(),
  cardQuickAdd: z.boolean().optional(),
  cartType: z.enum(['drawer', 'page']).optional(),
  predictiveSearch: z.boolean().optional(),
  /** How wide the cart drawer opens, in pixels; 0 or none leaves the shop's own width. */
  cartWidth: z.number().int().min(0).max(640).optional(),
  /** How long the cart drawer takes to slide in, in milliseconds; 0 or none leaves the shop's own speed. */
  cartSpeed: z.number().int().min(0).max(1000).optional(),
  /** The line between the cart's parts. */
  cartRule: z.enum(CART_RULES).optional(),
  /** Whether the cart's Check out opens the full checkout page, whatever the order form's own placement. */
  checkoutPage: z.boolean().optional()
}).strict();

export type ThemeOptionsDto = z.infer<typeof ThemeOptionsSchema>;

/** What every option means when neither the theme nor the store has set it. */
export const THEME_OPTION_DEFAULTS: Readonly<Required<ThemeOptionsDto>> = {
  logoWidth: 0, cardRatio: 'square', cardHoverImage: true, cardShowType: true, cardShowRating: true, cardQuickAdd: true,
  cartType: 'drawer', predictiveSearch: true, cartWidth: 0, cartSpeed: 0, cartRule: 'hairline', checkoutPage: false
};

// Reads a theme's options, each one it leaves out answered by its default.
export const themeOptions = (styles: { options?: ThemeOptionsDto } | null | undefined): Required<ThemeOptionsDto> =>
  ({ ...THEME_OPTION_DEFAULTS, ...styles?.options });
